import {
  Form,
  FormConfig,
  FormPage,
  FormQuestion,
  FormQuestionComponents,
  FormSubmission,
  PatientInfoTags,
  PDFExportForm,
  ScoringGroup,
  SubmissionLifeCycleState,
} from "@ambii-com/core";
import { DocumentSnapshot } from "@firebase/firestore-types";
import { Timestamp } from "firebase/firestore/lite";
import {
  liteCollections,
  liteGetDocument,
  liteGetDocumentsIn,
  liteQueryCollection,
  liteQueryCollectionLimited,
  liteQueryCollectionWith,
} from "~/utils/firestoreLite";

export async function readForm(formId: string): Promise<Form> {
  return liteGetDocument(liteCollections.forms, formId).then(Form.fromDoc);
}

export async function readFormConfig(
  formConfigId: string
): Promise<FormConfig> {
  return liteGetDocument(liteCollections.formConfigs, formConfigId).then(
    FormConfig.fromDoc
  );
}

export async function readFormConfigByShortLinkPath(
  shortLinkPath: string
): Promise<FormConfig | undefined> {
  const docs = await liteQueryCollectionLimited(
    liteCollections.formConfigs,
    1,
    ["shortLinkPath", "==", shortLinkPath]
  );
  if (docs.length === 0) {
    return undefined;
  }
  return FormConfig.fromDoc(docs[0]);
}

export async function readFormsForClinic(clinicId: string): Promise<Form[]> {
  const docs = await liteQueryCollection(
    liteCollections.forms,
    "clinicId",
    clinicId
  );
  return Form.fromDocs(docs);
}

export async function readFormPage(pageId: string): Promise<FormPage> {
  return liteGetDocument(liteCollections.formPages, pageId).then(
    FormPage.fromDoc
  );
}

export async function readFormPagesByFormId(
  formId: string
): Promise<FormPage[]> {
  const docs = await liteQueryCollectionWith(liteCollections.formPages, [
    "formId",
    "==",
    formId,
  ]);
  return FormPage.fromDocs(docs);
}

export async function readFormQuestion(
  questionId: string
): Promise<FormQuestion> {
  return liteGetDocument(liteCollections.formQuestions, questionId).then(
    FormQuestion.fromDoc
  );
}

export async function readFormQuestionsByFormId(
  formId: string
): Promise<FormQuestion[]> {
  const docs = await liteQueryCollectionWith(liteCollections.formQuestions, [
    "formId",
    "==",
    formId,
  ]);
  return FormQuestion.fromDocs(docs);
}

export async function readFormQuestionsByPageId(
  pageId: string
): Promise<FormQuestion[]> {
  const docs = await liteQueryCollectionWith(liteCollections.formQuestions, [
    "pageId",
    "==",
    pageId,
  ]);
  return FormQuestion.fromDocs(docs);
}

export async function readFormQuestionsByIds(
  questionIds: string[]
): Promise<FormQuestion[]> {
  const docs = await liteGetDocumentsIn(
    liteCollections.formQuestions,
    questionIds
  );
  return FormQuestion.fromDocs(docs);
}

export async function readFormSubmission(
  submissionId: string,
  formConfig?: FormConfig
): Promise<FormSubmission> {
  const doc = await liteGetDocument(
    liteCollections.formSubmissions,
    submissionId
  );
  return FormSubmission.fromDoc(doc, formConfig);
}

export async function readFormSubmissionsByIds(
  submissionIds: string[]
): Promise<FormSubmission[]> {
  const docs = await liteGetDocumentsIn(
    liteCollections.formSubmissions,
    submissionIds
  );
  return FormSubmission.fromDocs(docs);
}

export async function readFormSubmissionsByClinicAndDateRange(
  clinicId: string,
  startDate: Date,
  endDate: Date
): Promise<FormSubmission[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formSubmissions,
    ["clinicId", "==", clinicId],
    ["createdAt", ">=", Timestamp.fromDate(startDate)],
    ["createdAt", "<=", Timestamp.fromDate(endDate)]
  );
  return FormSubmission.fromDocs(docs);
}

export async function readActiveFormSubmissionsByClinicId(
  clinicId: string,
  formConfig?: FormConfig
): Promise<FormSubmission[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formSubmissions,
    ["clinicId", "==", clinicId],
    ["lifeCycleState", "in", [
      SubmissionLifeCycleState.CREATED,
      SubmissionLifeCycleState.UNARCHIVED,
    ]]
  );
  return FormSubmission.fromDocs(docs, formConfig);
}

export async function readArchivedFormSubmissionsByClinicId(
  clinicId: string,
  formConfig?: FormConfig
): Promise<FormSubmission[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formSubmissions,
    ["clinicId", "==", clinicId],
    ["lifeCycleState", "==", SubmissionLifeCycleState.ARCHIVED]
  );
  return FormSubmission.fromDocs(docs, formConfig);
}

export async function readCreatedFormSubmissionsByClinicId(
  clinicId: string,
  formConfig?: FormConfig
): Promise<FormSubmission[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formSubmissions,
    ["clinicId", "==", clinicId],
    ["lifeCycleState", "==", SubmissionLifeCycleState.CREATED]
  );
  return FormSubmission.fromDocs(docs, formConfig);
}

export async function readFormSubmissionExistsByAppointmentId(
  clinicId: string,
  appointmentId: string
): Promise<boolean> {
  const docs = await liteQueryCollectionLimited(
    liteCollections.formSubmissions,
    1,
    ["clinicId", "==", clinicId],
    ["appointmentId", "==", appointmentId]
  );
  return docs.length > 0;
}

export async function readFormSubmissionByAppointmentId(
  clinicId: string,
  appointmentId: string
): Promise<DocumentSnapshot | undefined> {
  const docs = await liteQueryCollectionLimited(
    liteCollections.formSubmissions,
    1,
    ["clinicId", "==", clinicId],
    ["appointmentId", "==", appointmentId]
  );
  return docs[0];
}

export async function formHasVisitReasonQuestion(
  formId: string,
  excludeQuestionId?: string
): Promise<boolean> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formQuestions,
    ["formId", "==", formId],
    ["componentType", "==", FormQuestionComponents.VISIT_REASON_FIELD]
  );
  return docs.filter((doc) => doc.id !== excludeQuestionId).length !== 0;
}

export async function readPatientInfoTagsForForm(
  formId: string,
  excludeQuestionId?: string
): Promise<PatientInfoTags[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.formQuestions,
    ["formId", "==", formId],
    ["dashboardMetadata.patientInfoTag", "!=", null]
  );
  return FormQuestion.fromDocs(docs)
    .filter((question) => question.id !== excludeQuestionId)
    .map((question) => question.dashboardMetadata.patientInfoTag);
}

export async function readScoringGroupsForClinic(
  clinicId: string
): Promise<ScoringGroup[]> {
  const docs = await liteQueryCollectionWith(liteCollections.scoringGroups, [
    "clinicId",
    "==",
    clinicId,
  ]);
  return ScoringGroup.fromDocs(docs);
}

export async function readNotDeletedScoringGroupsForClinic(
  clinicId: string
): Promise<ScoringGroup[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.scoringGroups,
    ["clinicId", "==", clinicId],
    ["deleted", "==", false]
  );
  return ScoringGroup.fromDocs(docs);
}

export async function readScoringGroup(
  scoringGroupId: string
): Promise<ScoringGroup | undefined> {
  const doc = await liteGetDocument(
    liteCollections.scoringGroups,
    scoringGroupId
  );
  if (!doc.exists) {
    return undefined;
  }
  return ScoringGroup.fromDoc(doc);
}

const pdfExportFormByFormIdCache = new Map<
  string,
  Promise<PDFExportForm | undefined>
>();

export async function readPDFExportFormByFormId(
  formId: string
): Promise<PDFExportForm | undefined> {
  if (!pdfExportFormByFormIdCache.has(formId)) {
    const promise = liteQueryCollectionLimited(
      liteCollections.pdfExportForms,
      1,
      ["formId", "==", formId]
    )
      .then((docs) => {
        if (docs.length === 0) {
          return undefined;
        }
        return PDFExportForm.fromDoc(docs[0]);
      })
      .catch((error) => {
        pdfExportFormByFormIdCache.delete(formId);
        throw error;
      });
    pdfExportFormByFormIdCache.set(formId, promise);
  }
  return pdfExportFormByFormIdCache.get(formId);
}

export function clearPDFExportFormCache(formId?: string): void {
  if (formId) {
    pdfExportFormByFormIdCache.delete(formId);
    return;
  }
  pdfExportFormByFormIdCache.clear();
}